import { z } from 'zod';
// Animation token registry (§9). Templates cite these by token; wave 3 wires
// each token to a concrete preset on the renderer side.
//
// Adding a new entrance/exit animation:
//   1. Add the token here.
//   2. Add a renderer-side preset under packages/remotion that maps the token
//      to actual interpolate/spring values.
export const ANIMATION_TOKENS = [
    // No entrance — element is fully visible immediately (static "just show"
    // templates like ins-outs).
    'none',
    'fade-in',
    // The slide RECIPE — any direction, via { dx, dy }. The four names below
    // are aliases onto it and stay because 22 files cite them.
    'slide',
    'slide-up',
    'slide-down',
    'slide-left',
    'slide-right',
    'pop',
    'spring-in',
    'ken-burns',
    'mask-wipe',
    'typewriter',
    // Reveals text one word at a time (each word fades + rises in), then holds.
    // Block-driven: the renderer splits the text and staggers the words.
    'word-by-word',
    'word-by-word-reverse',
    // Every LETTER fades in individually, but in a SCATTERED order rather than
    // left-to-right — the line assembles rather than sweeps. Block-driven, like
    // the two above: the renderer splits the text and times each glyph.
    //
    // The order is scattered, never random: it is derived from a hash of the
    // element's id + its text, so the same copy always assembles the same way
    // (see LETTER_SCATTER_* in ./motion-presets.ts for why that is not optional).
    'letter-fade-scattered',
    // Every LETTER springs in on its own, left to right, so the line TYPES rather
    // than fades. Block-driven like the three above.
    //
    // Each glyph arrives horizontally compressed and rotated off its resting
    // angle, overshoots PAST its resting width, and settles — the line staying
    // left-anchored throughout rather than re-centring on every character.
    //
    // Distinct from `typewriter`, which slices the string and pops each character
    // in flat; the glyphs here are laid out from the start and are transformed in
    // place, so several are in flight at once.
    //
    // The name is the reel's word for it, not a claim about the geometry: it is
    // NOT a rotation in depth, and LETTER_FLIP_OVERSHOOT_SCALE_X in
    // ./motion-presets.ts carries the measurement that rules one out.
    'letter-flip',
    // ── Parametric recipes ────────────────────────────────────────────
    //
    // These desugar through a FUNCTION of `motion.params` rather than a fixed
    // track list, because the same animation is genuinely used at different
    // timings. See MotionPreset in ./motion-presets.ts.
    //
    // A "ghost" is a translucent duplicate of a step card that drifts across
    // behind the real one. `step-by-step` carries two per step, and had six
    // hand-written copies of these two recipes between them.
    //
    // Enters by rotating upright out of the frame's bottom-left while rising.
    // `entryDelay` is frames after the block starts; `drift` is how long it
    // slides on afterwards.
    'ghost-rise',
    // The same card, entering flat from off the right edge.
    'ghost-fly-in',
    // ── Media transitions (shaped masks) ──────────────────────────────
    //
    // These reveal a BLOCK through an animated mask rather than moving it, so
    // they belong on a media-track or a solid, not on a line of text. Both were
    // observed in a real reel and were unbuildable until the mask channels
    // landed; see scripts/animation-catalogue.json for where each was seen.
    //
    // A vertical ellipse opening from the centre. What is behind it is a
    // composition concern — put a solid underneath for the classic black-to-
    // footage iris — because a mask reveals, it does not paint.
    'oval-iris',
    // Two rectangles forming an L: the top-right block arrives first and runs to
    // four fifths of the width, then the bottom-left block completes the shape.
    // Needs two mask channels because one channel cannot carry two clocks.
    'corner-l-wipe',
    // ── The BAR family ──────────────────────────────────────────────
    //
    // A band that grows across the frame from one edge. Added because a second
    // analysed reel measured seven shaped transitions and SIX of them fitted the
    // `rect` families with nothing in the library to name them — the coverage
    // ledger reported them as gaps, which is what a gap is for.
    //
    // Four edges rather than one parameterised token: a transition is cited by
    // NAME in a stored doc, and a name that carries a direction argument cannot
    // be linted against a closed set.
    'bar-wipe-up',
    'bar-wipe-down',
    'bar-wipe-right',
    'bar-wipe-left',
    // Two bars parting from the centre. This is the shape the one CONFIDENT
    // shaped transition in that reel actually fitted (rect2 gain 0.71, against
    // superellipse at -0.04 — i.e. an ellipse fitted it WORSE than flat alpha),
    // and until now it was named `oval-iris` because matching scored origins and
    // never asked what shape the measurement rejected.
    'split-bars',
    // Bands at the top and bottom NINTHS open to cover the outer thirds, retreat
    // onto their origin lines, and the frame resolves through a middle band and
    // a two-way horizontal slide — all while the outgoing clip darkens beneath.
    //
    // The only preset that drives a layer it is not applied to: the darkening
    // belongs to the OUTGOING clip, so it lives in `MASK_TRANSITION_OUTGOING`
    // rather than in this token's own tracks.
    //
    // Observed at reel-3 10-18%. Read the catalogue before re-tuning it: two of
    // its properties deliberately CONTRADICT the source (both slide directions,
    // and the middle band's start), so it is `intent-differs`, not `measured`.
    'thirds-cascade',
    // Two TRANSLUCENT diagonal wedges that scissor apart, the incoming clip
    // opening up in the widening band where they overlap.
    //
    // MEASURED off journey-2 at 5.1s (Canny + probabilistic Hough over f148-170,
    // clustered by angle and perpendicular offset). Two edges at MIRRORED angles,
    // dead steady at ±18.0° on screen for the whole run, coexisting for nine
    // frames — the +18° edge sweeping up (offset 1039 -> -18) and the -18° edge
    // sweeping down (379 -> 1332), crossing around f155.
    //
    // It is the first preset whose shape is neither axis-aligned nor opaque, and
    // it is what `MotionMaskRect.rotate` and `.alpha` were added for. The
    // translucency is not decoration: with `maskComposite: 'add'`, two wedges at
    // alpha 0.5 reveal the incoming clip FULLY where they cross and half
    // elsewhere, which is the effect. A feather cannot stand in for it — a
    // feather is a gradient at the boundary, this is flat alpha across the whole
    // wedge.
    'wedge-cross',
    // ONE opaque diagonal wedge. A triangle opens at the BOTTOM-LEFT corner and
    // its hypotenuse sweeps up and to the right until the incoming clip covers
    // the frame.
    //
    // STATED, NOT MEASURED, and the provenance matters because it is easy to
    // mistake for a measurement. It was asked for off `.full-renders/reel-2.mp4`
    // at 19-22%, where a wedge appears to slide up-and-right — but that render
    // draws PLACEHOLDER GRADIENT artwork, and the diagonal is painted into the
    // placeholder and panned by Ken Burns. It is not a transition, and reel-2's
    // source dissolves at that cut: recovering the per-pixel transition alpha
    // across f166-190 against the settled shots either side gives a smooth
    // 0.21 -> 0.84 ramp with no straight moving edge, and a block-level
    // which-shot-is-this map finds no split. So there is nothing to measure and
    // this preset does not claim to reproduce anything.
    //
    // What IS carried over from a measurement is the family: the angle is
    // expressed in the same stretched viewBox as `wedge-cross` and corrected the
    // same way, and the easing is `wedge-cross`'s measured deceleration rather
    // than a fresh guess, because it is the same kind of move and the only wedge
    // anyone has measured.
    //
    // OPAQUE, unlike `wedge-cross`. The translucency there is what makes two
    // scissoring wedges read as one opening band; a single wedge has nothing to
    // cross with, so a flat alpha would just render the whole reveal at half
    // strength.
    'wedge-up-right',
    // A reel's OPEN: the shot rises out of darkness, overshoots into a bloom, and
    // settles. Not a mask — there is no shape, so it needs nothing behind it.
    //
    // Brightness ONLY. The source also softens and desaturates at the peak, and
    // neither is in here: the softness did not survive a contrast-invariant
    // check (see the catalogue), and there is no saturation channel.
    'light-bloom-in',
];
export const animationRef = z.enum(ANIMATION_TOKENS);
//# sourceMappingURL=animation-tokens.js.map